import React from 'react';
import { 
  UsersIcon, ClipboardDocumentListIcon, BeakerIcon, HeartIcon, 
  CreditCardIcon, ShieldCheckIcon, BuildingOfficeIcon, TagIcon, UserPlusIcon
} from '@heroicons/react/24/outline';

interface StatsCardsProps {
  stats: any;
  isLoading: boolean;
  dateFilter?: 'today' | 'month' | 'custom';
}

export const StatsCards: React.FC<StatsCardsProps> = ({ stats, isLoading, dateFilter = 'today' }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-pulse">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-28 bg-white/60 rounded-2xl border border-white/80"></div>
        ))}
      </div>
    );
  }

  const getPeriodLabel = (todayText: string, monthText: string, customText: string) => {
    if (dateFilter === 'month') return monthText;
    if (dateFilter === 'custom') return customText;
    return todayText;
  };

  const coreCards = [
    {
      name: getPeriodLabel('Total Pasien Hari Ini', 'Total Pasien Bulan Ini', 'Total Pasien (Periode)'),
      stat: stats?.totalPatients || 0,
      icon: UsersIcon,
      glassBg: 'bg-gradient-to-br from-[#1782a2]/15 via-white/85 to-white/65',
      glassBorder: 'border border-[#1782a2]/30 hover:border-[#1782a2]/60',
      glassShadow: 'shadow-md shadow-[#1782a2]/10 hover:shadow-xl hover:shadow-[#1782a2]/20',
      iconGlass: 'bg-[#1782a2]/15 text-[#1782a2] border border-[#1782a2]/35',
      orbColor: 'bg-[#1782a2]/30',
      labelColor: 'text-[#1782a2]',
    },
    {
      name: getPeriodLabel('Kunjungan Hari Ini', 'Kunjungan Bulan Ini', 'Kunjungan (Periode)'),
      stat: stats?.todaysVisits || 0,
      icon: ClipboardDocumentListIcon,
      glassBg: 'bg-gradient-to-br from-emerald-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-emerald-300/40 hover:border-emerald-400/70',
      glassShadow: 'shadow-md shadow-emerald-500/10 hover:shadow-xl hover:shadow-emerald-500/20',
      iconGlass: 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/35',
      orbColor: 'bg-emerald-500/30',
      labelColor: 'text-emerald-600',
    },
    {
      name: 'Hasil Tertunda',
      stat: stats?.pendingLabResults || 0,
      icon: BeakerIcon,
      glassBg: 'bg-gradient-to-br from-amber-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-amber-300/40 hover:border-amber-400/70',
      glassShadow: 'shadow-md shadow-amber-500/10 hover:shadow-xl hover:shadow-amber-500/20',
      iconGlass: 'bg-amber-500/15 text-amber-600 border border-amber-500/35',
      orbColor: 'bg-amber-500/30',
      labelColor: 'text-amber-600',
    },
    {
      name: getPeriodLabel('Resep Hari Ini', 'Resep Bulan Ini', 'Resep (Periode)'),
      stat: stats?.prescriptionsToday || 0,
      icon: HeartIcon,
      glassBg: 'bg-gradient-to-br from-rose-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-rose-300/40 hover:border-rose-400/70',
      glassShadow: 'shadow-md shadow-rose-500/10 hover:shadow-xl hover:shadow-rose-500/20',
      iconGlass: 'bg-rose-500/15 text-rose-600 border border-rose-500/35',
      orbColor: 'bg-rose-500/30',
      labelColor: 'text-rose-600',
    },
  ];

  const paymentCards = [
    { 
      name: 'Umum', 
      stat: stats.paymentMethods?.umum || 0, 
      icon: CreditCardIcon, 
      glassBg: 'bg-gradient-to-br from-[#1782a2]/15 via-white/85 to-white/65',
      glassBorder: 'border border-[#1782a2]/30 hover:border-[#1782a2]/60',
      glassShadow: 'shadow-md shadow-[#1782a2]/10',
      iconGlass: 'bg-[#1782a2]/15 text-[#1782a2] border border-[#1782a2]/35',
      orbColor: 'bg-[#1782a2]/30',
      labelColor: 'text-[#1782a2]',
    },
    { 
      name: 'BPJS', 
      stat: stats.paymentMethods?.bpjs || 0, 
      icon: ShieldCheckIcon, 
      glassBg: 'bg-gradient-to-br from-emerald-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-emerald-300/40 hover:border-emerald-400/70',
      glassShadow: 'shadow-md shadow-emerald-500/10',
      iconGlass: 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/35',
      orbColor: 'bg-emerald-500/30',
      labelColor: 'text-emerald-600',
    },
    { 
      name: 'Asuransi', 
      stat: stats.paymentMethods?.asuransi || 0, 
      icon: ShieldCheckIcon, 
      glassBg: 'bg-gradient-to-br from-sky-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-sky-300/40 hover:border-sky-400/70',
      glassShadow: 'shadow-md shadow-sky-500/10',
      iconGlass: 'bg-sky-500/15 text-sky-600 border border-sky-500/35',
      orbColor: 'bg-sky-500/30',
      labelColor: 'text-sky-600',
    },
    { 
      name: 'Perusahaan', 
      stat: stats.paymentMethods?.perusahaan || 0, 
      icon: BuildingOfficeIcon, 
      glassBg: 'bg-gradient-to-br from-amber-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-amber-300/40 hover:border-amber-400/70',
      glassShadow: 'shadow-md shadow-amber-500/10',
      iconGlass: 'bg-amber-500/15 text-amber-600 border border-amber-500/35',
      orbColor: 'bg-amber-500/30',
      labelColor: 'text-amber-600',
    },
    { 
      name: 'Paket Promosi', 
      stat: stats.paymentMethods?.paketPromosi || 0, 
      icon: TagIcon, 
      glassBg: 'bg-gradient-to-br from-rose-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-rose-300/40 hover:border-rose-400/70',
      glassShadow: 'shadow-md shadow-rose-500/10',
      iconGlass: 'bg-rose-500/15 text-rose-600 border border-rose-500/35',
      orbColor: 'bg-rose-500/30',
      labelColor: 'text-rose-600',
    },
    { 
      name: 'Rujukan', 
      stat: stats.paymentMethods?.rujukan || 0, 
      icon: UserPlusIcon, 
      glassBg: 'bg-gradient-to-br from-violet-500/15 via-white/85 to-white/65',
      glassBorder: 'border border-violet-300/40 hover:border-violet-400/70',
      glassShadow: 'shadow-md shadow-violet-500/10',
      iconGlass: 'bg-violet-500/15 text-violet-600 border border-violet-500/35',
      orbColor: 'bg-violet-500/30',
      labelColor: 'text-violet-600',
    },
  ];

  return (
    <div className="space-y-6 mb-8">
      {/* Core Stats */}
      <div>
        <h3 className="text-base md:text-lg font-extrabold text-slate-800 tracking-tight mb-4">Ringkasan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {coreCards.map((c, i) => (
            <GlassStatCard key={i} {...c} />
          ))}
        </div>
      </div>

      {/* Payment Methods */}
      <div className="pt-6 border-t border-slate-200/60">
        <h3 className="text-base md:text-lg font-extrabold text-slate-800 tracking-tight mb-4">
          Kunjungan Berdasarkan Metode Pembayaran
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {paymentCards.map((c, i) => (
            <GlassStatCard key={i} {...c} />
          ))}
        </div>
      </div>

      {/* Poli & Layanan */}
      <div className="pt-6 border-t border-slate-200/60">
        <h3 className="text-base md:text-lg font-extrabold text-slate-800 tracking-tight mb-4">
          Statistik Layanan (Poli & Penunjang)
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Kiri: Lab, Rad, Apotik, Gigi, Kandungan */}
          <div className="relative overflow-hidden backdrop-blur-xl bg-white/75 border border-white/80 shadow-lg shadow-slate-200/40 rounded-2xl md:rounded-3xl p-6">
            <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-white to-transparent pointer-events-none"></div>
            <h4 className="font-extrabold text-slate-800 text-sm md:text-base mb-4 pb-2 border-b border-slate-200/60">
              Rincian Poliklinik & Medis
            </h4>
            <div className="space-y-3 text-sm">
              <StatRow label="Dokter Umum" value={stats.services?.umum} />
              <StatRow label="Dokter Gigi" value={stats.services?.gigi} />
              <StatRow label="Kebidanan" value={stats.services?.kebidanan} />
              <StatRow label="Apotik (Tanpa Dokter)" value={stats.services?.apotikMandiri} />
              <StatRow label="Vaksin (Travel)" value={stats.services?.vaksinTravel} />
              <StatRow label="Vaksin (Mandiri)" value={stats.services?.vaksinMandiri} />
              
              {stats.services?.vaksinNames && Object.keys(stats.services.vaksinNames).length > 0 && (
                <div className="pl-4 border-l-2 border-[#1782a2]/30 mt-2 space-y-1">
                  {Object.entries(stats.services.vaksinNames).map(([name, count]) => (
                    <div key={name} className="flex justify-between items-center py-0.5 text-xs">
                      <span className="text-slate-500 italic">{name}</span>
                      <span className="font-bold text-slate-700 bg-white/80 px-2 py-0.5 rounded-lg border border-slate-200/60">{String(count)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <StatRow label="Laboratorium (Rekomendasi Dokter)" value={stats.services?.labDokter} />
                <StatRow label="Laboratorium (Permintaan Mandiri)" value={stats.services?.labMandiri} />
                <StatRow label="Laboratorium (Perusahaan)" value={stats.services?.labPerusahaan} />
                <StatRow label="Laboratorium (Rujukan)" value={stats.services?.labRujukan} />
                <StatRow label="Radiologi (Rekomendasi Dokter)" value={stats.services?.radDokter} />
                <StatRow label="Radiologi (Permintaan Mandiri)" value={stats.services?.radMandiri} />
                <StatRow label="Radiologi (Perusahaan)" value={stats.services?.radPerusahaan} />
                <StatRow label="Radiologi (Rujukan)" value={stats.services?.radRujukan} />
              </div>
            </div>
          </div>

          {/* Kanan: Penunjang EKG dll */}
          <div className="relative overflow-hidden backdrop-blur-xl bg-white/75 border border-white/80 shadow-lg shadow-slate-200/40 rounded-2xl md:rounded-3xl p-6 flex flex-col">
            <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-white to-transparent pointer-events-none"></div>
            <h4 className="font-extrabold text-slate-800 text-sm md:text-base mb-4 pb-2 border-b border-slate-200/60">
              Pemeriksaan Penunjang
            </h4>
            <div className="flex-1 flex flex-col justify-between space-y-3 pb-2">
              <SupportingStat title="EKG" data={stats.supportingTests?.ekg} />
              <SupportingStat title="Treadmill" data={stats.supportingTests?.treadmill} />
              <SupportingStat title="Audiometri" data={stats.supportingTests?.audiometri} />
              <SupportingStat title="Spirometri" data={stats.supportingTests?.spirometri} />
              <SupportingStat title="Refraksi" data={stats.supportingTests?.refraksi} />
            </div>
            <h4 className="font-extrabold text-slate-800 text-sm md:text-base mt-4 mb-2 pt-3 border-t border-slate-200/60">
              Rincian Radiologi
            </h4>
            <div className="flex-1 flex flex-col justify-between space-y-2 pb-2">
              <SupportingStat title="Rontgen (X-Ray)" data={stats.radiologyBreakdown?.xray} />
              <RadiologyItems items={stats.radiologyBreakdown?.xray?.items} />
              <SupportingStat title="USG" data={stats.radiologyBreakdown?.usg} />
              <RadiologyItems items={stats.radiologyBreakdown?.usg?.items} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Glass Stat Card Component
const GlassStatCard = ({ 
  name, stat, icon: Icon, glassBg, glassBorder, glassShadow, iconGlass, orbColor, labelColor 
}: any) => (
  <div className={`relative overflow-hidden ${glassBg} backdrop-blur-xl p-4 sm:p-5 rounded-2xl md:rounded-3xl ${glassBorder} ${glassShadow} transition-all duration-300 hover:-translate-y-1 group`}>
    {/* Specular line */}
    <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-white/90 to-transparent pointer-events-none"></div>
    {/* Corner ambient orb */}
    <div className={`absolute -right-5 -bottom-5 w-20 h-20 rounded-full ${orbColor} blur-xl opacity-40 group-hover:opacity-75 transition-opacity pointer-events-none`}></div>

    <div className="relative z-10 flex items-center space-x-3.5">
      <div className={`${iconGlass} p-3 rounded-2xl backdrop-blur-md shadow-2xs group-hover:scale-110 transition-transform duration-300 shrink-0`}>
        <Icon className="h-5 w-5 sm:h-6 sm:w-6 stroke-[2.2]" aria-hidden="true" />
      </div>
      <div className="w-0 flex-1 min-w-0">
        <dt className={`text-[11px] font-extrabold ${labelColor || 'text-slate-500'} uppercase tracking-wider truncate mb-0.5`}>
          {name}
        </dt>
        <dd className="text-xl sm:text-2xl font-black text-slate-900 font-tabular-nums">
          {stat || 0}
        </dd>
      </div>
    </div>
  </div>
);

const StatRow = ({ label, value }: { label: string, value: any }) => (
  <div className="flex justify-between items-center py-1">
    <span className="text-slate-600 text-xs sm:text-sm font-medium">{label}</span>
    <span className="font-bold text-slate-800 bg-white/80 border border-slate-200/60 px-2.5 py-0.5 rounded-lg text-xs font-tabular-nums">
      {value || 0}
    </span>
  </div>
);

const SupportingStat = ({ title, data }: { title: string, data: any }) => (
  <div>
    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{title}</div>
    <div className="grid grid-cols-3 gap-2">
      <div className="bg-[#1782a2]/10 border border-[#1782a2]/20 text-[#1782a2] text-xs px-2 py-1 rounded-xl text-center font-medium">
        Dokter: <b className="font-black">{data?.doctor || 0}</b>
      </div>
      <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 text-xs px-2 py-1 rounded-xl text-center font-medium">
        Mandiri: <b className="font-black">{data?.patient || 0}</b>
      </div>
      <div className="bg-amber-500/10 border border-amber-500/20 text-amber-700 text-xs px-2 py-1 rounded-xl text-center font-medium">
        PT: <b className="font-black">{data?.company || 0}</b>
      </div>
    </div>
  </div>
);

const RadiologyItems = ({ items }: { items?: Array<{ bodyPart: string, status: string, date: string }> }) => {
  if (!items || items.length === 0) return (
    <div className="text-xs text-slate-400 italic mb-2 ml-1">Tidak ada pemeriksaan pada rentang tanggal ini</div>
  );

  const groupedItems = items.reduce((acc, item) => {
    const part = (item.bodyPart || 'Unknown').toLowerCase();
    if (!acc[part]) {
      acc[part] = { 
        count: 0, 
        status: item.status, 
        label: (item.bodyPart || 'Unknown').charAt(0).toUpperCase() + (item.bodyPart || 'Unknown').slice(1) 
      };
    }
    acc[part]!.count += 1;
    if (item.status !== 'completed') acc[part]!.status = item.status;
    return acc;
  }, {} as Record<string, { count: number, status: string, label: string }>);

  return (
    <div className="mb-2 ml-1 mt-1">
      <div className="flex flex-wrap gap-1.5">
        {Object.values(groupedItems).map((item, i) => (
          <span key={i} className={`inline-flex items-center text-xs px-2.5 py-0.5 rounded-full border font-semibold ${
            item.status === 'completed' 
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}>
            {item.label} <span className="ml-1 opacity-75">({item.count})</span>
          </span>
        ))}
      </div>
    </div>
  );
};
